import type { DepartmentResponse } from '../models/Department';
import type { OptionProps, SubOptionProps, ProvinceTypes } from '../models/OptionProps';
import type { SecurityList } from '../models/SecurityList';
import type { Person } from '../models/Person';
import type { ExtraEmail, DistributionListIds } from '../models/DistributionList';
import { ALL_CREW_LIST_ID } from './distribution/constants';
import DistributionService from './distribution/DistributionService';
import type { AuthUser } from './auth/constants';
import { SecurityListIds, DHDisplayName } from '../models/SecurityList';
import type { Subscription } from '../models/Subscription';
import CrooglooSubscriptionService from './subscription/CrooglooSubscriptionService';
import { store } from '../state/store';

/**
 * Service to handle the process of changing objects into option props interface for select dropdpowns
 */
interface IOptionPropsService {
  itemsToOptionProps: (emptyItem: string, items: string[]) => OptionProps[]
  departmentsToOptionProps: (departments: DepartmentResponse[]) => OptionProps[]
  provincesToSubOptionProps: (provinces: ProvinceTypes[]) => SubOptionProps[]
  securityListsToOptionProps: (securityLists: SecurityList[], disableAdmin: boolean) => OptionProps[]
  personToOptionProps: (persons: Person[]) => OptionProps[]
  extraEmailsToOptionProps: (extraEmails: ExtraEmail[]) => OptionProps[]
  distributionListsToOptionProps: (distributionLists: DistributionListIds[]) => OptionProps[]
  distributionGroupListToOptionProps: (distributionGroupList: any) => OptionProps[]
  subscriptionToOptionProps: (productList: Subscription[], disableActiveProduct: boolean) => OptionProps[]
  productionsToOptionProps: (productions: string[]) => OptionProps[]
}

class OptionPropsService implements IOptionPropsService {
  /**
   * Common function to change an array of strings to Option Props for select dropdowns
   * @param items {string[]}
   */
  itemsToOptionProps (emptyItem: string, items: string[]): OptionProps[] {
    return [
      { key: 'option-none', value: '', label: emptyItem },
      ...items.map((item: string) => ({ key: `option-${item}`, value: item, label: item }))
    ];
  }

  /**
   * DepartmentResponse to Option Props
   * Example of usage is in basic info for add contact
   * @param departments {DepartmentResponse[]}
   */
  departmentsToOptionProps (departments: DepartmentResponse[]): OptionProps[] {
    let options: OptionProps[] = [];
    if (departments.length > 0) {
      departments.forEach((department: DepartmentResponse) => {
        options.push({
          key: department.key.name,
          value: department.key.name,
          label: department.properties.title.trim().toUpperCase()
        });
      })
    }

    options = options.sort((a: OptionProps, b: OptionProps) => a.label.localeCompare(b.label));

    return options;
  }

  /**
   * Provinces Object to Sub Option Props
   * Example of usage is in more info for add contact
   * @param provinces {ProvinceTypes[]}
   */
  provincesToSubOptionProps (provinces: ProvinceTypes[]): SubOptionProps[] {
    if (provinces.length === 0) return [];
    const options: SubOptionProps[] = provinces.map((province: ProvinceTypes) => {
      return {
        key: province.key,
        options: [
          ...province.provinces.map((value: string) => ({ key: `province-${value}`, value, label: value }))
        ]
      }
    });
    return options;
  }

  /**
   * Security Lists Object to Option Props
   * Example of usage is in basic info for add contact
   * @param securityLists {SecurityList[]}
   */
  securityListsToOptionProps (securityLists: SecurityList[], disableAdmin: boolean): OptionProps[] {
    const crooglooAuth: AuthUser = store.getState().auth.crooglooauth;
    const options: OptionProps[] = [];
    if (securityLists.length > 0) {
      securityLists.forEach((securityList: SecurityList) => {
        const disabled: boolean = (disableAdmin)
          ? !!((securityList.securityListId === SecurityListIds.ADMIN) ||
          (crooglooAuth.securityListId && securityList.securityListId.toLowerCase() === crooglooAuth.securityListId.toLowerCase()))
          : false;
        options.push({
          key: securityList.securityListId,
          value: securityList.securityListId,
          label: (securityList.securityListName === SecurityListIds.DH) ? DHDisplayName : securityList.securityListName,
          disabled
        })
      });
    }
    return options;
  }

  /**
   * Person Object to Option Props
   * Example of usage is in basic info for add contact
   * @param persons {Person[]}
   */
  personToOptionProps (persons: Person[]): OptionProps[] {
    const options: OptionProps[] = [];
    if (persons.length > 0) {
      persons.forEach((person: Person) => {
        if (person.id) {
          options.push({
            key: (person.id) ?? '',
            value: (person.id) ?? '',
            label: `${person.firstName} ${person.lastName} (${person.email})`
          })
        }
      });
    }
    return options;
  }

  /**
   * ExtraEmail Object to Option Props
   * Example of usage is in slide drawer for compose page
   * @param extraEmails {ExtraEmail[]}
   */
  extraEmailsToOptionProps (extraEmails: ExtraEmail[]): OptionProps[] {
    return extraEmails.map((extraEmail: ExtraEmail) => ({
      key: extraEmail.email, value: extraEmail.email, label: extraEmail.label
    }));
  }

  /**
   * DistributionListIds Obkect to Option Props
   * Example of usage is in Compare Lists in Distribution Lists Page
   * @param distributionLists: DistributionListIds[]
   */
  distributionListsToOptionProps (distributionLists: DistributionListIds[]): OptionProps[] {
    return distributionLists.map((list: DistributionListIds) => ({
      key: list.id, value: list.id, label: list.distributionListName
    }))
  }

  /**
   * distributionGroupList Object to Option Props
   * Example of usage is in Merge Lists in Distribution Lists Page
   * @param distributionGroupList: any
   */
  distributionGroupListToOptionProps (distributionGroupList: any): OptionProps[] {
    const options: OptionProps[] = [];
    Object.keys(distributionGroupList).forEach((id: string) => {
      const disabled: boolean = (DistributionService.getMemberCount(id) === 0 || id === ALL_CREW_LIST_ID);
      options.push({
        key: id, value: id, label: distributionGroupList[id], disabled
      })
    });
    return options.sort(sortByDisabled);
  }

  /**
   * array of Subscription objects to array of OptionpProps
   * disable the current plan if disableActiveProduct is set to true
   * @param productList: Subscription[]
   * @param disableActiveProduct: boolean
   */
  subscriptionToOptionProps (productList: Subscription[], disableActiveProduct: boolean): OptionProps[] {
    const currentPlan: Subscription | null = store.getState().subscription.currentPlan;
    const options: OptionProps[] = [];
    productList.forEach((product: Subscription) => {
      const disabled: boolean = (disableActiveProduct) ? product.planId === currentPlan?.planId : false;
      options.push({
        key: product.planId,
        value: product.planId,
        label: product.name + ' ($' + CrooglooSubscriptionService.getPriceAsString(product) + ')',
        disabled
      })
    })
    return options;
  }

  /**
   * array of production ids to array of option props
   * @param productions
   */
  productionsToOptionProps (productions: string[]): OptionProps[] {
    const crooglooAuth: AuthUser = store.getState().auth.crooglooauth;
    return productions.map((production: string) => ({
      key: production,
      value: production,
      label: crooglooAuth.communitiesIdNameMap?.[production] ?? production
    }));
  }
}

/**
 * function to sort optionProps[] with disabled values at top of dropdown
 * @param optionA
 * @param optionB
 */
function sortByDisabled (optionA: OptionProps, optionB: OptionProps) {
  return (optionA.disabled === optionB.disabled) ? 0 : optionB.disabled ? 1 : -1;
}

const optionPropsService: IOptionPropsService = new OptionPropsService();
export default optionPropsService;
